/**
 * TemplateLibraryBrowser — reusable modal for browsing CADENCE_SCRIPTS.
 *
 * Drop-in component used from BDR cadence, AE outreach, ad-hoc compose,
 * account detail, and the standalone /templates browse page.
 *
 * Palette: Mi6 Black #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points, no emojis.
 */
import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  CADENCE_SCRIPTS,
  type Persona,
  type ScriptChannel,
  type TemplateScript,
} from "@/lib/template-library";

const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";
const AIP_BG = "#FAFAF7";

const CHANNEL_LABELS: Record<ScriptChannel, string> = {
  email_opening: "Email Opening",
  email_followup: "Email Follow-up",
  linkedin_dm: "LinkedIn DM",
  phone_script: "Phone Script",
  voicemail_script: "Voicemail",
};

const PERSONA_LABELS: Record<Persona, string> = {
  finance: "Finance",
  operations: "Operations",
  hr: "HR",
};

const ALL_CHANNELS: ScriptChannel[] = [
  "email_opening",
  "email_followup",
  "linkedin_dm",
  "phone_script",
  "voicemail_script",
];
const ALL_PERSONAS: Persona[] = ["finance", "operations", "hr"];

export interface TemplateLibraryBrowserProps {
  open: boolean;
  onClose: () => void;
  initialChannel?: ScriptChannel;
  initialPersona?: Persona;
  onPick: (script: TemplateScript) => void;
  title?: string;
  /**
   * Optional CTA label override on the per-card button.
   * Default: "Use this template".
   */
  pickLabel?: string;
  /**
   * Optional secondary action: copy to clipboard.
   * When provided, a "Copy to clipboard" button appears alongside the primary action.
   */
  onCopy?: (script: TemplateScript) => void;
}

function ChannelBadge({ channel }: { channel: ScriptChannel }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: "#FAFAF7",
        color: "#0B0B0F",
        border: "1px solid #E5E5E7",
      }}
    >
      {CHANNEL_LABELS[channel]}
    </span>
  );
}

function PersonaBadge({ persona }: { persona: Persona }) {
  const palette: Record<Persona, { bg: string; fg: string; border: string }> = {
    finance: { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" },
    operations: { bg: "#DCFCE7", fg: "#166534", border: "#BBF7D0" },
    hr: { bg: "#FEE2E2", fg: "#0B0B0F", border: "#FEE2E2" },
  };
  const p = palette[persona];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: p.bg,
        color: p.fg,
        border: `1px solid ${p.border}`,
      }}
    >
      {PERSONA_LABELS[persona]}
    </span>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="whitespace-nowrap"
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.02em",
        cursor: "pointer",
        border: active ? `1px solid ${MI6_BLACK}` : "1px solid #E5E5E7",
        background: active ? MI6_BLACK : "#FFFFFF",
        color: active ? "#FFFFFF" : "#525258",
        transition: "all 120ms ease",
      }}
    >
      {label}
    </button>
  );
}

function firstLines(text: string, n = 2): string {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, n)
    .join(" ");
}

export default function TemplateLibraryBrowser({
  open,
  onClose,
  initialChannel,
  initialPersona,
  onPick,
  title = "Template Library",
  pickLabel = "Use this template",
  onCopy,
}: TemplateLibraryBrowserProps) {
  const [query, setQuery] = useState("");
  const [channels, setChannels] = useState<ScriptChannel[]>(
    initialChannel ? [initialChannel] : []
  );
  const [personas, setPersonas] = useState<Persona[]>(
    initialPersona ? [initialPersona] : []
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Reset filters when modal re-opens with new initial filters
  useEffect(() => {
    if (open) {
      setChannels(initialChannel ? [initialChannel] : []);
      setPersonas(initialPersona ? [initialPersona] : []);
      setQuery("");
      setExpandedId(null);
    }
  }, [open, initialChannel, initialPersona]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CADENCE_SCRIPTS.filter((s) => {
      if (channels.length > 0 && !channels.includes(s.channel)) return false;
      if (personas.length > 0 && !personas.includes(s.persona)) return false;
      if (!q) return true;
      const haystack = [
        s.title,
        s.subject || "",
        s.body,
        s.notes || "",
      ]
        .join("\n")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, channels, personas]);

  function toggleChannel(c: ScriptChannel) {
    setChannels((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
  }
  function togglePersona(p: Persona) {
    setPersonas((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  }

  function handlePick(s: TemplateScript) {
    onPick(s);
    onClose();
  }

  if (!open) return null;

  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        style={{ background: AIP_BG }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-neutral-200 flex-shrink-0 bg-white"
          style={{ borderTop: `3px solid ${MI6_RED}` }}
        >
          <div>
            <div
              className="text-[9px] uppercase tracking-[0.22em] font-bold mb-0.5"
              style={{ color: MI6_RED }}
            >
              Library
            </div>
            <div className="text-base font-bold" style={{ color: MI6_BLACK }}>
              {title}
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {filtered.length} of {CADENCE_SCRIPTS.length} templates
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors flex-shrink-0 ml-4"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex-shrink-0 px-5 py-3 bg-white border-b border-neutral-200 space-y-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, subject, body, or notes"
            className="w-full text-sm rounded-md border border-neutral-200 px-3 py-2 focus:outline-none focus:border-neutral-400"
            style={{ color: MI6_BLACK }}
          />
          <div className="flex flex-wrap gap-2">
            <span
              className="text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-500 self-center mr-1"
            >
              Channel
            </span>
            {ALL_CHANNELS.map((c) => (
              <FilterPill
                key={c}
                label={CHANNEL_LABELS[c]}
                active={channels.includes(c)}
                onClick={() => toggleChannel(c)}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className="text-[10px] uppercase tracking-[0.16em] font-semibold text-neutral-500 self-center mr-1"
            >
              Persona
            </span>
            {ALL_PERSONAS.map((p) => (
              <FilterPill
                key={p}
                label={PERSONA_LABELS[p]}
                active={personas.includes(p)}
                onClick={() => togglePersona(p)}
              />
            ))}
            {(channels.length > 0 || personas.length > 0 || query) && (
              <button
                type="button"
                onClick={() => {
                  setChannels([]);
                  setPersonas([]);
                  setQuery("");
                }}
                className="ml-auto self-center"
                style={{
                  fontSize: 11,
                  color: MI6_RED,
                  fontWeight: 600,
                  textDecoration: "underline",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {filtered.length === 0 && (
            <div
              className="text-center py-10 text-sm"
              style={{ color: "#6B6B70" }}
            >
              No templates match these filters.
            </div>
          )}
          {filtered.map((s) => {
            const expanded = expandedId === s.id;
            return (
              <div
                key={s.id}
                className="bg-white rounded-lg border border-neutral-200 transition-shadow hover:shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : s.id)}
                  className="w-full text-left px-4 py-3"
                  style={{ background: "transparent", border: "none", cursor: "pointer" }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-bold leading-snug"
                        style={{ color: MI6_BLACK }}
                      >
                        {s.title}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <ChannelBadge channel={s.channel} />
                        <PersonaBadge persona={s.persona} />
                        <span
                          style={{
                            fontSize: 10,
                            color: "#8E8E94",
                            fontFamily: "monospace",
                            alignSelf: "center",
                          }}
                        >
                          {s.id}
                        </span>
                      </div>
                      {s.subject && (
                        <div
                          className="text-xs mt-2"
                          style={{ color: "#525258" }}
                        >
                          <span className="font-semibold">Subject: </span>
                          {s.subject}
                        </div>
                      )}
                      <div
                        className="text-xs mt-1 line-clamp-2"
                        style={{ color: "#6B6B70" }}
                      >
                        {firstLines(s.body, 2)}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 18,
                        color: "#8E8E94",
                        flexShrink: 0,
                        transform: expanded ? "rotate(90deg)" : "none",
                        transition: "transform 120ms ease",
                      }}
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-neutral-100 px-4 py-3 space-y-3 bg-neutral-50">
                    {s.subject && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                          Subject
                        </div>
                        <div
                          className="text-sm font-medium"
                          style={{ color: MI6_BLACK }}
                        >
                          {s.subject}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                        Body
                      </div>
                      <div
                        className="text-sm whitespace-pre-wrap leading-relaxed bg-white border border-neutral-200 rounded-md p-3"
                        style={{ color: "#3F3F44" }}
                      >
                        {s.body}
                      </div>
                    </div>
                    {s.tokens.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                          Tokens
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {s.tokens.map((t) => (
                            <span
                              key={t}
                              style={{
                                fontFamily: "monospace",
                                fontSize: 11,
                                padding: "2px 6px",
                                borderRadius: 4,
                                background: "#FEF2F2",
                                color: MI6_RED,
                                border: "1px solid #FECACA",
                              }}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {s.notes && (
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.16em] font-semibold text-neutral-500 mb-1">
                          When to use
                        </div>
                        <div
                          className="text-xs leading-relaxed"
                          style={{ color: "#525258" }}
                        >
                          {s.notes}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 pt-1 flex-wrap">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePick(s);
                        }}
                        className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-white transition-all hover:shadow-md active:scale-95"
                        style={{ background: MI6_BLACK }}
                      >
                        {pickLabel}
                      </button>
                      {onCopy && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCopy(s);
                          }}
                          className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all hover:shadow-md active:scale-95"
                          style={{ background: "#EFEFEE", color: MI6_BLACK, border: "1px solid #E5E5E7" }}
                        >
                          Copy to Clipboard
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedId(null);
                        }}
                        className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
                        style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
                      >
                        Collapse
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-neutral-200 px-5 py-3 flex items-center justify-between bg-white">
          <div className="text-[11px] text-neutral-500">
            Click a card to preview · Use this template to insert
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider text-neutral-500"
            style={{ background: "#EFEFEE", border: "1px solid #E5E5E7" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
